import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublicHealthReport from "./reports/PublicHealthReport";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Badge, Empty, Table, Td } from "../components/UI";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Users, ClipboardList, CalendarCheck, FileText, Download, Play, AlertTriangle, Search, Sparkles } from "lucide-react";
import { useV2 } from "../context/V2Context";
import ComingSoon from "./ComingSoon";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COCOA = ["#4E342E", "#8D6E63", "#BCAAA4", "#FFB300", "#2E7D32", "#5E35B1", "#1565C0"];
const TAB_GROUPS = [
  ["Clinical", [["kpi", "KPI Dashboard"], ["clients", "All Clients"], ["compliance", "PMR Compliance"], ["expiring", "Expiring Documents"], ["public_health", "Public Health"]]],
  ["Financial", [["financial", "Financial Report"], ["utilization", "Utilization & Payer Mix"]]],
  ["Patient", [["population", "Patient Population 360"], ["allergies", "Allergies Report"]]],
  ["Operations", [["productivity", "Staff Productivity"], ["attendance", "Attendance & No-Shows"], ["referrals", "Referral Funnel"]]],
  ["Compliance", [["audit", "Audit Trail"], ["custom", "Custom Report Builder"]]],
];
const TABS = TAB_GROUPS.flatMap(([, t]) => t);

function FinancialReport() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/financial").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading…</div>;
  return (
    <div data-testid="report-financial" className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Billed", d.totals.billed], ["Collected", d.totals.collected], ["Outstanding", d.totals.outstanding], ["Collection rate", `${d.totals.collection_rate}%`]].map(([l, v]) => (
          <Card key={l} className="p-4"><div className="text-xs uppercase font-semibold text-[#7B6157]">{l}</div>
            <div className="font-heading text-2xl font-bold text-[#3E2723]">{typeof v === "number" ? `$${v.toLocaleString()}` : v}</div></Card>
        ))}
      </div>
      <ChartCard title="Billed vs Collected by month">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={d.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => `$${v.toLocaleString()}`} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="revenue" name="Billed" fill="#4E342E" radius={[5, 5, 0, 0]} /><Bar dataKey="collected" name="Collected" fill="#BCAAA4" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <Card className="overflow-hidden">
        <Table headers={["Payer", "Authorizations"]}>
          {d.payer_mix.map((p) => <tr key={p.payer} className="border-t border-[#F1EEE7]"><Td className="font-medium">{p.payer}</Td><Td>{p.count}</Td></tr>)}
        </Table>
      </Card>
    </div>
  );
}

function PopulationReport() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/population").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading…</div>;
  return (
    <div data-testid="report-population" className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={`Age distribution (${d.total} clients)`}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={d.age_bands}><CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis dataKey="band" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="count" fill="#4E342E" radius={[5, 5, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Gender & status mix">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={d.gender} dataKey="count" nameKey="gender" outerRadius={70} label>
                {d.gender.map((_, i) => <Cell key={i} fill={["#4E342E", "#8D6E63", "#BCAAA4", "#FFB300"][i % 4]} />)}
              </Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <Card className="overflow-hidden">
        <Table headers={["Top Diagnoses", "Clients"]}>
          {d.top_diagnoses.map((x) => <tr key={x.diagnosis} className="border-t border-[#F1EEE7]"><Td className="font-medium">{x.diagnosis}</Td><Td>{x.count}</Td></tr>)}
        </Table>
      </Card>
      {d.programs.length > 0 && (
        <Card className="overflow-hidden">
          <Table headers={["Program", "Clients"]}>
            {d.programs.map((x) => <tr key={x.program} className="border-t border-[#F1EEE7]"><Td className="font-medium">{x.program}</Td><Td>{x.count}</Td></tr>)}
          </Table>
        </Card>
      )}
    </div>
  );
}

const SEV_STYLE = { "Life-Threatening": "bg-[#B71C1C] text-white", Severe: "bg-[#FFEBEE] text-[#C62828]", Moderate: "bg-[#FFF3E0] text-[#B84A00]", Mild: "bg-[#E8F5E9] text-[#2E7D32]", Minimal: "bg-[#E8F5E9] text-[#2E7D32]" };

function AllergiesReport() {
  const [d, setD] = useState(null);
  const [sev, setSev] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/reports/allergies").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const shown = useMemo(() => {
    if (!d) return [];
    const qq = q.trim().toLowerCase();
    return d.rows.filter((r) => (!sev || (r.severity || "Unspecified") === sev) &&
      (!qq || `${r.client} ${r.mrn} ${r.allergen} ${r.reaction} ${r.type}`.toLowerCase().includes(qq)));
  }, [d, sev, q]);
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading…</div>;
  const sevCount = (k) => (d.by_severity.find((x) => x.severity === k) || {}).count || 0;
  return (
    <div data-testid="report-allergies" className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Stat icon={AlertTriangle} label="Severe allergies" value={sevCount("Severe")} tone="bg-[#FFEBEE] text-[#C62828]" />
        <Stat icon={AlertTriangle} label="Moderate" value={sevCount("Moderate")} tone="bg-[#FFF3E0] text-[#B84A00]" />
        <Stat icon={AlertTriangle} label="Mild" value={sevCount("Mild")} tone="bg-[#E8F5E9] text-[#2E7D32]" />
        <Stat icon={Users} label="Clients affected" value={d.clients_with_allergies} />
        <Stat icon={ClipboardList} label="Allergy records" value={d.rows.length} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
          <FInput data-testid="allergies-search" className="h-10 w-56 pl-9" placeholder="Search client, allergen…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {["", ...d.by_severity.map((x) => x.severity)].map((k) => (
          <button key={k || "all"} data-testid={`allergies-sev-${(k || "all").toLowerCase()}`} onClick={() => setSev(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${sev === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            {k || "All severities"}
          </button>
        ))}
        <Btn variant="outline" data-testid="allergies-export-btn" className="ml-auto px-3 py-1.5 text-xs"
          onClick={() => toCSV(["client", "mrn", "allergen", "type", "reaction", "severity"], shown, "allergies-report")}><Download size={13} /> Export CSV</Btn>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <Table headers={["Client", "MRN", "Allergen", "Type", "Reaction", "Severity"]}>
            {shown.map((r, i) => (
              <tr key={i} className="border-t border-[#F1EEE7]" data-testid={`allergy-row-${i}`}>
                <Td className="font-medium">{r.client}</Td><Td>{r.mrn}</Td><Td>{r.allergen}</Td><Td>{r.type || "-"}</Td><Td>{r.reaction || "-"}</Td>
                <Td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${SEV_STYLE[r.severity] || "bg-[#F5F2EB] text-[#7B6157]"}`}>{r.severity || "Unspecified"}</span></Td>
              </tr>
            ))}
          </Table>
          {shown.length === 0 && <div className="p-6 text-center text-sm text-[#84675B]">No allergy records match</div>}
        </Card>
        <div className="space-y-4">
          <Card className="p-4" data-testid="allergies-chart-severity">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold text-[#3E2723]">Severity distribution</h3>
              <span className="text-[10px] text-[#7B6157]">{d.rows.length} records</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={d.by_severity} dataKey="count" nameKey="severity" outerRadius={70} label={(x) => x.severity}>
                  {d.by_severity.map((s, i) => (
                    <Cell key={s.severity} fill={
                      s.severity === "Severe" ? "#C62828" :
                      s.severity === "Life-Threatening" ? "#8E0000" :
                      s.severity === "Moderate" ? "#E65100" :
                      s.severity === "Mild" ? "#2E7D32" :
                      s.severity === "Minimal" ? "#66BB6A" : COCOA[i % COCOA.length]
                    } />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4" data-testid="allergies-chart-top-allergens">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold text-[#3E2723]">Top 8 allergens</h3>
              <span className="text-[10px] text-[#7B6157]">by client count</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.by_allergen.slice(0, 8)} layout="vertical" margin={{ left: 12, right: 12, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                <XAxis type="number" stroke="#8D6E63" fontSize={10} />
                <YAxis type="category" dataKey="allergen" stroke="#8D6E63" fontSize={10} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#4E342E" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="overflow-hidden">
            <Table headers={["Severity", "Count"]}>
              {d.by_severity.map((x) => <tr key={x.severity} className="border-t border-[#F1EEE7]"><Td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${SEV_STYLE[x.severity] || "bg-[#F5F2EB] text-[#7B6157]"}`}>{x.severity}</span></Td><Td>{x.count}</Td></tr>)}
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
}

const toCSV = (columns, rows, name) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [columns.map(esc).join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}-${dayjs().format("YYYYMMDD")}.csv`;
  a.click();
};

const Stat = ({ icon: Icon, label, value, tone = "bg-[#EFEBE0] text-[#4E342E]" }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-[#EFEBE0] bg-white p-4 flex-1 min-w-[160px]">
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={18} /></span>
    <div><div className="font-heading text-xl font-bold text-[#3E2723]">{value}</div><div className="text-[11px] text-[#7B6157]">{label}</div></div>
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <Card className="p-5" data-testid={testId}>
    <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">{title}</h3>
    <div className="h-64">{children}</div>
  </Card>
);

function KpiDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/kpis").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="py-16 text-center text-sm text-[#84675B]">Computing KPIs…</div>;
  const c = data.cards;
  const trend = data.admissions_trend.map((t) => ({ ...t, month: dayjs(t.month + "-01").format("MMM") }));
  const taskData = [{ name: "On track", value: Math.max(0, c.open_tasks - c.overdue_tasks) }, { name: "Overdue", value: c.overdue_tasks }, { name: "Completed", value: c.completed_tasks }];
  return (
    <div className="space-y-5" data-testid="kpi-dashboard">
      <div className="flex flex-wrap gap-3">
        <Stat icon={Users} label="Active clients" value={c.active_clients} />
        <Stat icon={CalendarCheck} label="Group attendance rate" value={`${c.attendance_rate}%`} tone="bg-[#E8F5E9] text-[#2E7D32]" />
        <Stat icon={ClipboardList} label="Groups this month" value={c.groups_this_month} />
        <Stat icon={AlertTriangle} label="Overdue tasks" value={c.overdue_tasks} tone={c.overdue_tasks ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#E8F5E9] text-[#2E7D32]"} />
        <Stat icon={FileText} label="Clinical documents" value={c.clinical_documents} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard title="Admissions · Discharges · Referrals (6 months)" testId="chart-trend">
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8D6E63" }} />
              <YAxis tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
              <Tooltip /><Legend />
              <Line type="monotone" dataKey="admissions" stroke="#4E342E" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="discharges" stroke="#FFB300" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="referrals" stroke="#8D6E63" strokeWidth={2} strokeDasharray="5 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Active Census by Program" testId="chart-programs">
          <ResponsiveContainer>
            <BarChart data={data.by_program} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis dataKey="program" tick={{ fontSize: 10, fill: "#8D6E63" }} interval={0} />
              <YAxis tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.by_program.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Client Status Mix" testId="chart-status">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.status_mix} dataKey="count" nameKey="status" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {data.status_mix.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Task Throughput" testId="chart-tasks">
          <ResponsiveContainer>
            <BarChart data={taskData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#8D6E63" }} width={80} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                <Cell fill="#8D6E63" /><Cell fill="#C62828" /><Cell fill="#2E7D32" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function AllClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  const filtered = clients.filter((c) => `${c.first_name} ${c.last_name} ${c.mrn}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <div data-testid="report-all-clients">
      <div className="mb-3 flex items-center gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
          <FInput data-testid="report-clients-search" placeholder="Search clients…" className="pl-9 h-10 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Btn variant="outline" data-testid="export-clients-csv" className="rounded-lg ml-auto" onClick={() => toCSV(["first_name", "last_name", "mrn", "status", "gender", "dob", "diagnosis", "admission_date"], filtered, "all-clients")}>
          <Download size={14} /> Export CSV
        </Btn>
      </div>
      <Card>
        <Table headers={["Client", "MRN", "DOB", "Gender", "Diagnosis", "Admission", "Status"]}>
          {filtered.map((c) => (
            <tr key={c.id} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => navigate(`/clients/${c.id}`)}>
              <Td className="font-semibold">{c.first_name} {c.last_name}</Td>
              <Td>{c.mrn}</Td><Td>{c.dob}</Td><Td>{c.gender}</Td>
              <Td className="max-w-[220px] truncate">{c.diagnosis}</Td>
              <Td>{(c.admission_date || "").slice(0, 10)}</Td>
              <Td><Badge value={c.status} /></Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function ComplianceReport() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reports/compliance").then((r) => setRows(r.data)); }, []);
  if (!rows) return <div className="py-16 text-center text-sm text-[#84675B]">Scoring charts…</div>;
  return (
    <div data-testid="report-compliance">
      <div className="mb-3 flex justify-end">
        <Btn variant="outline" data-testid="export-compliance-csv" className="rounded-lg" onClick={() => toCSV(["client", "mrn", "score"], rows, "pmr-compliance")}><Download size={14} /> Export CSV</Btn>
      </div>
      <Card>
        <Table headers={["Client", "MRN", "Chart Compliance", "Failing Checks"]}>
          {rows.map((r) => (
            <tr key={r.client_id} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => navigate(`/clients/${r.client_id}/console`)}>
              <Td className="font-semibold">{r.client}</Td>
              <Td>{r.mrn}</Td>
              <Td className="w-64">
                <span className="flex items-center gap-2">
                  <span className="h-2 flex-1 rounded-full bg-[#F5F2EB] overflow-hidden">
                    <span className={`block h-full rounded-full ${r.score >= 80 ? "bg-[#2E7D32]" : r.score >= 50 ? "bg-[#FFB300]" : "bg-[#C62828]"}`} style={{ width: `${r.score}%` }} />
                  </span>
                  <span className="tabular-nums font-bold text-sm w-10">{r.score}%</span>
                </span>
              </Td>
              <Td className="text-xs text-[#7B6157] max-w-[300px] truncate">{r.failing.join(", ") || "-"}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function ExpiringDocs() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reports/expiring").then((r) => setRows(r.data)); }, []);
  if (!rows) return <div className="py-16 text-center text-sm text-[#84675B]">Scanning documents…</div>;
  return (
    <div data-testid="report-expiring">
      <Card>
        <Table headers={["Type", "Client", "Document / Service", "Expires", "Status"]}>
          {rows.map((r, i) => (
            <tr key={i} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => r.client_id && navigate(`/clients/${r.client_id}/console`)}>
              <Td className="font-semibold">{r.type}</Td>
              <Td>{r.client}</Td>
              <Td className="max-w-[260px] truncate">{r.title}</Td>
              <Td className="tabular-nums">{r.expires || "-"}</Td>
              <Td><Badge value={r.status} /></Td>
            </tr>
          ))}
        </Table>
        {rows.length === 0 && <Empty text="Nothing expiring in the next 45 days - great job!" />}
      </Card>
    </div>
  );
}

function Productivity() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/reports/productivity").then((r) => setRows(r.data)); }, []);
  if (!rows) return <div className="py-16 text-center text-sm text-[#84675B]">Aggregating staff activity…</div>;
  return (
    <div className="space-y-4" data-testid="report-productivity">
      <ChartCard title="Output by Staff (notes · groups · completed tasks)" testId="chart-productivity">
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8D6E63" }} interval={0} />
            <YAxis tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
            <Tooltip /><Legend />
            <Bar dataKey="notes" name="Progress notes" fill="#4E342E" radius={[4, 4, 0, 0]} />
            <Bar dataKey="groups" name="Groups facilitated" fill="#FFB300" radius={[4, 4, 0, 0]} />
            <Bar dataKey="tasks_completed" name="Tasks completed" fill="#8D6E63" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <div className="flex justify-end"><Btn variant="outline" data-testid="export-productivity-csv" className="rounded-lg" onClick={() => toCSV(["name", "role", "notes", "groups", "tasks_completed", "tracked_hours"], rows, "staff-productivity")}><Download size={14} /> Export CSV</Btn></div>
      <Card>
        <Table headers={["Staff", "Role", "Progress Notes", "Groups Facilitated", "Tasks Completed", "Tracked Hours"]}>
          {rows.map((r) => (
            <tr key={r.id}><Td className="font-semibold">{r.name}</Td><Td className="capitalize">{r.role.replace("_", " ")}</Td>
              <Td className="tabular-nums">{r.notes}</Td><Td className="tabular-nums">{r.groups}</Td>
              <Td className="tabular-nums">{r.tasks_completed}</Td><Td className="tabular-nums">{r.tracked_hours}h</Td></tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function AttendanceReport() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reports/attendance").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-16 text-center text-sm text-[#84675B]">Computing attendance…</div>;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="report-attendance">
      <Card data-testid="attendance-by-group">
        <h3 className="font-heading text-sm font-bold text-[#3E2723] p-5 pb-2">By Group Session (completed)</h3>
        <Table headers={["Date", "Group", "Facilitator", "Present", "No-Shows", "Rate"]}>
          {data.groups.map((g, i) => (
            <tr key={i}><Td className="tabular-nums">{g.date}</Td><Td className="font-semibold max-w-[180px] truncate">{g.name}</Td>
              <Td>{g.facilitator}</Td><Td className="tabular-nums">{g.present}/{g.roster}</Td>
              <Td className="tabular-nums">{g.no_shows}</Td>
              <Td><span className={`font-bold ${g.rate >= 80 ? "text-[#2E7D32]" : g.rate >= 50 ? "text-[#8D5A00]" : "text-[#C62828]"}`}>{g.rate}%</span></Td></tr>
          ))}
        </Table>
        {data.groups.length === 0 && <Empty text="No completed group sessions yet" />}
      </Card>
      <Card data-testid="attendance-by-client">
        <h3 className="font-heading text-sm font-bold text-[#3E2723] p-5 pb-2">By Client (lowest attendance first)</h3>
        <Table headers={["Client", "Attended", "Absent", "No-Shows", "Rate"]}>
          {data.clients.map((c) => (
            <tr key={c.client_id} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => navigate(`/clients/${c.client_id}/console`)}>
              <Td className="font-semibold">{c.client}</Td><Td className="tabular-nums">{c.attended}</Td>
              <Td className="tabular-nums">{c.absent}</Td><Td className="tabular-nums">{c.no_shows}</Td>
              <Td><span className={`font-bold ${c.rate >= 80 ? "text-[#2E7D32]" : c.rate >= 50 ? "text-[#8D5A00]" : "text-[#C62828]"}`}>{c.rate}%</span></Td></tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function ReferralFunnel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/referrals").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-16 text-center text-sm text-[#84675B]">Building funnel…</div>;
  return (
    <div className="space-y-4" data-testid="report-referrals">
      <div className="flex flex-wrap gap-3">
        {data.funnel.map((f, i) => (
          <div key={f.stage} className="flex-1 min-w-[160px] rounded-2xl border border-[#EFEBE0] bg-white p-5 text-center">
            <div className="font-heading text-3xl font-bold text-[#3E2723]">{f.count}</div>
            <div className="text-xs text-[#7B6157] mt-1">{f.stage}</div>
            {i > 0 && data.funnel[i - 1].count > 0 && <div className="text-[11px] font-bold text-[#7B6157] mt-1">{Math.round(f.count / data.funnel[i - 1].count * 100)}% of previous</div>}
          </div>
        ))}
        <div className="flex-1 min-w-[160px] rounded-2xl bg-[#4E342E] text-white p-5 text-center" data-testid="conversion-rate-card">
          <div className="font-heading text-3xl font-bold">{data.conversion_rate}%</div>
          <div className="text-xs text-white/70 mt-1">Referral → Admission conversion</div>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Referrals by Source" testId="chart-referral-source">
          <ResponsiveContainer>
            <BarChart data={data.by_source} layout="vertical" margin={{ top: 5, right: 20, left: 30, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
              <YAxis type="category" dataKey="source" tick={{ fontSize: 10, fill: "#8D6E63" }} width={130} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>{data.by_source.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Referral Status Mix" testId="chart-referral-status">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.by_status} dataKey="count" nameKey="status" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {data.by_status.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function Utilization() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reports/utilization").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-16 text-center text-sm text-[#84675B]">Loading utilization…</div>;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start" data-testid="report-utilization">
      <Card className="xl:col-span-2" data-testid="utilization-auths">
        <h3 className="font-heading text-sm font-bold text-[#3E2723] p-5 pb-2">Authorization Utilization (units used vs approved)</h3>
        <Table headers={["Client", "Service", "Auth #", "Used / Approved", "Utilization", "Ends", "Status"]}>
          {data.authorizations.map((a, i) => (
            <tr key={i} className="cursor-pointer hover:bg-[#FDFCF8]" onClick={() => a.client_id && navigate(`/clients/${a.client_id}/console`)}>
              <Td className="font-semibold">{a.client}</Td>
              <Td className="max-w-[160px] truncate">{a.service}</Td>
              <Td>{a.auth_number}</Td>
              <Td className="tabular-nums">{a.used} / {a.approved}</Td>
              <Td className="w-44">
                <span className="flex items-center gap-2">
                  <span className="h-2 flex-1 rounded-full bg-[#F5F2EB] overflow-hidden">
                    <span className={`block h-full rounded-full ${a.pct >= 90 ? "bg-[#C62828]" : a.pct >= 70 ? "bg-[#FFB300]" : "bg-[#2E7D32]"}`} style={{ width: `${Math.min(a.pct, 100)}%` }} />
                  </span>
                  <span className="tabular-nums text-xs font-bold w-9">{a.pct}%</span>
                </span>
              </Td>
              <Td className="tabular-nums">{a.end_date}</Td>
              <Td><Badge value={a.status} /></Td>
            </tr>
          ))}
        </Table>
        {data.authorizations.length === 0 && <Empty text="No authorizations on file" />}
      </Card>
      <ChartCard title="Payer Mix (clients by insurance)" testId="chart-payer-mix">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data.payer_mix} dataKey="count" nameKey="payer" innerRadius={50} outerRadius={85} paddingAngle={3}>
              {data.payer_mix.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
            </Pie>
            <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function AuditTrail() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (action) params.action = action;
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/reports/audit-trail", { params }).then((r) => { setRows(r.data); setErr(""); })
      .catch((e) => setErr(apiError(e)));
  }, [q, action, start, end]);
  if (err) return <Empty text={err} />;
  if (!rows) return <div className="py-16 text-center text-sm text-[#84675B]">Loading audit trail…</div>;
  return (
    <div data-testid="report-audit">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-3 text-[#84675B]" />
          <FInput data-testid="audit-search" placeholder="Search user, action, resource…" className="pl-9 h-10 w-64" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <FSelect data-testid="audit-action-filter" className="h-10 w-40" value={action} onChange={(e) => setAction(e.target.value)} placeholder="All actions">
          {["login", "view", "create", "update", "delete", "archive", "print"].map((a) => <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>)}
        </FSelect>
        <FInput data-testid="audit-start-date" type="date" className="h-10 w-40" value={start} onChange={(e) => setStart(e.target.value)} />
        <FInput data-testid="audit-end-date" type="date" className="h-10 w-40" value={end} onChange={(e) => setEnd(e.target.value)} />
        <Btn variant="outline" data-testid="export-audit-csv" className="rounded-lg ml-auto" onClick={() => toCSV(["timestamp", "user_email", "action", "resource", "detail"], rows, "audit-trail")}><Download size={14} /> Export CSV</Btn>
      </div>
      <div className="mb-3 text-xs text-[#7B6157]">HIPAA access log - every login, chart view, edit, print & archive is recorded</div>
      <Card>
        <Table headers={["Timestamp", "User", "Action", "Resource", "Detail"]}>
          {rows.map((r) => (
            <tr key={r.id}>
              <Td className="tabular-nums whitespace-nowrap text-xs">{dayjs(r.timestamp).format("MM/DD/YY HH:mm:ss")}</Td>
              <Td className="text-xs">{r.user_email}</Td>
              <Td><span className="rounded-full bg-[#F5F2EB] px-2.5 py-0.5 text-xs font-semibold capitalize">{r.action}</span></Td>
              <Td className="text-xs">{r.resource}</Td>
              <Td className="text-xs max-w-[280px] truncate">{r.detail}</Td>
            </tr>
          ))}
        </Table>
        {rows.length === 0 && <Empty text="No audit entries match" />}
      </Card>
    </div>
  );
}

const ENTITY_FIELDS = {
  clients: ["first_name", "last_name", "mrn", "status", "gender", "dob", "city", "state", "diagnosis", "admission_date"],
  tasks: ["number", "title", "project", "module", "priority", "status", "assigned_to_name", "due_date", "estimated_hours"],
  groups: ["date", "name", "group_type", "facilitator", "program", "cpt_code", "location", "status", "start_time"],
  authorizations: ["auth_number", "service", "program", "status", "start_date", "end_date", "units_approved", "units_used"],
  referrals: ["status", "referral_source", "created_at"],
};

function CustomBuilder() {
  const [entity, setEntity] = useState("clients");
  const [fields, setFields] = useState(ENTITY_FIELDS.clients);
  const [status, setStatus] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [groupBy, setGroupBy] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFields(ENTITY_FIELDS[entity]); setGroupBy(""); setResult(null); }, [entity]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/custom", { entity, fields, status, start, end, group_by: groupBy });
      setResult(r.data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div data-testid="custom-report-builder">
      <Card className="p-5 mb-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-3 items-end">
          <div><FLabel>Data Source</FLabel>
            <FSelect data-testid="custom-entity-select" value={entity} onChange={(e) => setEntity(e.target.value)}>
              {Object.keys(ENTITY_FIELDS).map((e2) => <option key={e2} value={e2}>{e2[0].toUpperCase() + e2.slice(1)}</option>)}
            </FSelect></div>
          <div><FLabel>Status filter</FLabel><FInput data-testid="custom-status-input" placeholder="e.g. active" value={status} onChange={(e) => setStatus(e.target.value)} /></div>
          <div><FLabel>From</FLabel><DateField testId="custom-start" value={start} onChange={setStart} /></div>
          <div><FLabel>To</FLabel><DateField testId="custom-end" value={end} disabledBefore={start || undefined} onChange={setEnd} /></div>
          <div><FLabel>Group by</FLabel>
            <FSelect data-testid="custom-groupby-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {ENTITY_FIELDS[entity].map((f) => <option key={f} value={f}>{f}</option>)}
            </FSelect></div>
        </div>
        <div className="mt-3">
          <FLabel>Columns</FLabel>
          <div className="flex flex-wrap gap-2 mt-1">
            {ENTITY_FIELDS[entity].map((f) => {
              const on = fields.includes(f);
              return (
                <button key={f} type="button" data-testid={`custom-field-${f}`} onClick={() => setFields((fs) => (on ? fs.filter((x) => x !== f) : [...fs, f]))}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#E5DFD3] bg-white text-[#6D4C41] hover:border-[#8D6E63]"}`}>{f}</button>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          {result && <Btn variant="outline" data-testid="custom-export-csv" className="rounded-lg" onClick={() => toCSV(result.columns, result.rows, `custom-${entity}`)}><Download size={14} /> Export CSV</Btn>}
          <Btn data-testid="custom-run-btn" onClick={run} disabled={busy || fields.length === 0}><Play size={14} /> {busy ? "Running…" : "Run Report"}</Btn>
        </div>
      </Card>
      {result && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <Card className={result.grouped ? "xl:col-span-2" : "xl:col-span-3"}>
            <div className="p-4 pb-0 text-sm text-[#7B6157]" data-testid="custom-result-total">{result.total} rows</div>
            <div className="overflow-x-auto">
              <Table headers={result.columns}>
                {result.rows.slice(0, 100).map((r, i) => (
                  <tr key={i}>{result.columns.map((col) => <Td key={col} className="max-w-[180px] truncate">{String(r[col] ?? "")}</Td>)}</tr>
                ))}
              </Table>
            </div>
            {result.rows.length === 0 && <Empty text="No rows match the filters" />}
          </Card>
          {result.grouped && (
            <ChartCard title={`Count by ${groupBy}`} testId="custom-grouped-chart">
              <ResponsiveContainer>
                <BarChart data={result.grouped} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                  <XAxis dataKey="value" tick={{ fontSize: 10, fill: "#8D6E63" }} interval={0} />
                  <YAxis tick={{ fontSize: 12, fill: "#8D6E63" }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {result.grouped.map((_, i) => <Cell key={i} fill={COCOA[i % COCOA.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
}

const V2_TABS = { financial: "Financial Report", utilization: "Utilization & Payer Mix", population: "Patient Population 360", productivity: "Staff Productivity", audit: "Audit Trail", custom: "Custom Report Builder" };

export default function Reports() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "kpi");
  const { v2Enabled } = useV2();
  const tabCurtained = (k) => v2Enabled === false && !!V2_TABS[k];
  return (
    <div>
      <PageHeader title="Reports" subtitle="Categorized clinical, financial, patient & operations reporting"
        action={<Btn variant="outline" data-testid="export-pdf-btn" onClick={() => {
          document.body.classList.add("print-report");
          window.print();
          setTimeout(() => document.body.classList.remove("print-report"), 500);
        }}><Download size={14} /> Export PDF</Btn>} />
      <div className="mb-5 space-y-2">
        {TAB_GROUPS.map(([cat, tabs]) => (
          <div key={cat} className="flex items-center gap-2 overflow-x-auto">
            <span className="w-24 shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#84675B]" data-testid={`report-cat-${cat.toLowerCase()}`}>{cat}</span>
            {tabs.map(([k, l]) => (
              <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setTab(k)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
                {l}
                {tabCurtained(k) && <span className="inline-flex items-center gap-0.5 rounded-full bg-[#FFB300] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#3E2723]"><Sparkles size={9} /> V2</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div data-print-area>
      {tabCurtained(tab) ? <ComingSoon label={V2_TABS[tab]} /> : (
        <>
          {tab === "kpi" && <KpiDashboard />}
          {tab === "clients" && <AllClients />}
          {tab === "compliance" && <ComplianceReport />}
          {tab === "expiring" && <ExpiringDocs />}
          {tab === "productivity" && <Productivity />}
          {tab === "attendance" && <AttendanceReport />}
          {tab === "referrals" && <ReferralFunnel />}
          {tab === "utilization" && <Utilization />}
          {tab === "audit" && <AuditTrail />}
          {tab === "custom" && <CustomBuilder />}
          {tab === "financial" && <FinancialReport />}
          {tab === "population" && <PopulationReport />}
          {tab === "allergies" && <AllergiesReport />}
          {tab === "public_health" && <PublicHealthReport />}
        </>
      )}
      </div>
    </div>
  );
}
